/* eslint-disable @typescript-eslint/ban-ts-comment */
import React, {
  useEffect,
  useRef,
  InputHTMLAttributes,
  useCallback,
  useState,
} from 'react';
import { useField } from '@unform/core';

import { Container } from './styles';

interface inputErrorProps {
  inputName: string;
  message: string;
}

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  name: string;
  icon?: string;
  label?: string;
  iconError?: string;
  width?: number | string;
  handleInputError?(data: inputErrorProps): void;
  messageErrorOnBlur?: string;
  isDisabled?: boolean;
  isHidden?: boolean;
  fullWidth?: boolean;
  isInteger?: boolean;
  isMandatory?: boolean;
  mask?: (value: string) => string;
}

const Input: React.FC<InputProps> = ({
  name,
  handleInputError,
  icon,
  label,
  iconError,
  messageErrorOnBlur,
  isDisabled,
  isHidden,
  fullWidth,
  isInteger,
  isMandatory,
  mask,
  ...rest
}) => {
  const inputRef = useRef(null);

  const [success, setSuccess] = useState(false);
  const [inputValueCurrent, setInputValueCurrent] = useState<string>('');
  const { fieldName, defaultValue, registerField, error, clearError } =
    useField(name);

  useEffect(() => {
    registerField({
      name: fieldName,
      ref: inputRef.current,
      path: 'value',
      setValue: (ref, value) => {
        ref.value = value;
        setSuccess(false);
        clearError();
      },
    });
  }, [fieldName, registerField, clearError, inputValueCurrent]);

  const handleBlur = useCallback(
    event => {
      if (!event.target.value) {
        if (!!handleInputError && !!messageErrorOnBlur) {
          handleInputError({
            inputName: name,
            message: messageErrorOnBlur,
          });
        }
      } else setSuccess(true);
    },
    [handleInputError, messageErrorOnBlur, name]
  );

  return (
    <Container error={!!error} success={success} fullWidth={fullWidth}>
      {isHidden ? (
        <input type="hidden" />
      ) : (
        <>
          {label ? (
            <div className="label">
              <label>{label}</label>
            </div>
          ) : null}
          <div className="input-wrapper">
            {(rest.required || isMandatory) && (
              <span className="requiredIcon">*</span>
            )}
            {/* @ts-ignore */}
            <input
              {...rest}
              name={name}
              ref={inputRef}
              defaultValue={defaultValue}
              onBlur={handleBlur}
              disabled={isDisabled || rest.disabled || false}
              onFocus={() => {
                clearError();
                setSuccess(false);
              }}
              onKeyPress={() => {
                clearError();
              }}
              onKeyDown={e => {
                if (rest.type === 'number' && isInteger) {
                  if (!/^[0-9]$/.test(e.key) && e.key !== 'Backspace') {
                    e.preventDefault();
                  }
                }
              }}
              onChange={e => {
                const value = mask ? mask(e.target.value) : e.target.value;

                e.target.value = value;

                setInputValueCurrent(value);

                if (rest.onChange) {
                  rest.onChange(e);
                }
              }}
              type={rest.type || 'text'}
            />
            {!!icon && (
              <div className="icon">
                <img src={error ? iconError || icon : icon} alt={name} />
              </div>
            )}
          </div>
          {!!error && messageErrorOnBlur && (
            <div className="error">
              <p>{error}</p>
            </div>
          )}
        </>
      )}
    </Container>
  );
};

export default Input;
